import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";

const PRAZDNA_ADRESA = { ulice: "", psc: "", mesto: "" };

// Založení i úprava případu. `pripad` = úprava (klient se nemění).
// Adresa případu je místo realizace — u chaty nebo novostavby jiná než
// bydliště klienta, proto se z klienta jen nabízí k převzetí.
export default function PripadForm({ clientId = null, client = null, clients = [], pripad = null, onClose, onSaved }) {
  const uprava = !!pripad;
  const [nazev, setNazev] = useState(pripad?.nazev || "");
  const [adresa, setAdresa] = useState({ ...PRAZDNA_ADRESA, ...(pripad?.adresa || {}) });
  const [cid, setCid] = useState(pripad?.client_id || clientId);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Obchodník nového případu dostane jeho úkoly — výchozí podle Nastavení → Workflow úkolů.
  const [obchodnik, setObchodnik] = useState("");
  const [tym, setTym] = useState([]);
  useEffect(() => {
    if (uprava) return;
    api.get("/api/team").then((r) => setTym((r.data.members || []).filter((m) => m.active && m.role !== "SUBDODAVATEL"))).catch(() => {});
    api.get("/api/obchod/vychozi-obchodnik").then((r) => setObchodnik((o) => o || r.data.obchodnik_id || "")).catch(() => {});
  }, [uprava]);

  const selected = clients.find((c) => c.id === cid);
  const klient = client || selected || null;
  const adresaKlienta = klient?.adresa || {};
  const lzePrevzit = !!(adresaKlienta.ulice || adresaKlienta.mesto);
  const updA = (k) => (e) => setAdresa({ ...adresa, [k]: e.target.value });
  const filtered = clients.filter((c) => {
    const s = q.toLowerCase();
    return !s || (c.display_name || "").toLowerCase().includes(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!cid) return setError("Vyber klienta.");
    if (!nazev.trim()) return setError("Zadej název případu.");
    setSaving(true);
    try {
      const { data } = uprava
        ? await api.patch(`/api/obchodni-pripady/${pripad.id}`, { nazev: nazev.trim(), adresa })
        : await api.post("/api/obchodni-pripady", { client_id: cid, nazev: nazev.trim(), adresa, obchodnik_id: obchodnik || null });
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="pripad-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[460px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="border-b border-line-softer px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="t-sekce font-800 text-ink">{uprava ? "Upravit případ" : "Nový obchodní případ"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="pripad-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Název případu">
            <input className="field" value={nazev} onChange={(e) => setNazev(e.target.value)}
              placeholder="Např. Rodinný dům — TČ + FVE" data-testid="pripad-nazev" />
          </Field>
          {!clientId && !uprava && (
            <div>
              <span className="label-up text-muted block mb-1.5">Klient</span>
              {selected ? (
                <div className="field flex items-center justify-between">
                  <span>{selected.display_name}</span>
                  <button type="button" onClick={() => setCid(null)} className="text-[12px] text-muted hover:text-danger">Změnit</button>
                </div>
              ) : (
                <div className="border border-line-strong rounded-input overflow-hidden">
                  <input className="w-full px-3 h-10 bg-inputbg outline-none text-[13px]" placeholder="Hledat klienta…"
                    value={q} onChange={(e) => setQ(e.target.value)} data-testid="pripad-client-search" />
                  <div className="max-h-40 overflow-y-auto">
                    {filtered.slice(0, 10).map((c) => (
                      <button type="button" key={c.id} onClick={() => setCid(c.id)} data-testid={`pripad-client-${c.id}`}
                        className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-inputbg border-t border-line-softer">
                        {c.display_name} <span className="text-muted">· {c.adresa?.mesto}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <span className="label-up text-muted">Adresa realizace</span>
              {lzePrevzit && (
                <button type="button" onClick={() => setAdresa({ ...PRAZDNA_ADRESA, ...adresaKlienta })}
                  className="tap md:min-h-0 t-meta font-600 text-accent" data-testid="pripad-adresa-klienta">
                  Převzít z klienta
                </button>
              )}
            </div>
            <div className="space-y-2">
              <input className="field" value={adresa.ulice} onChange={updA("ulice")} placeholder="Ulice a č.p."
                aria-label="Ulice a č.p." data-testid="pripad-ulice" />
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <input className="field amount" value={adresa.psc} onChange={updA("psc")} placeholder="PSČ"
                  aria-label="PSČ" inputMode="numeric" data-testid="pripad-psc" />
                <input className="field" value={adresa.mesto} onChange={updA("mesto")} placeholder="Město / obec"
                  aria-label="Město" data-testid="pripad-mesto" />
              </div>
            </div>
            <span className="block mt-1 t-meta text-muted-2">Kde se montuje — u chaty nebo novostavby jiná než adresa klienta.</span>
          </div>
          {!uprava && tym.length > 1 && (
            <Field label="Obchodník" hint="Dostane úkoly případu (kontaktovat klienta, nabídka…).">
              <select className="field" value={obchodnik} onChange={(e) => setObchodnik(e.target.value)} data-testid="pripad-obchodnik-novy">
                {obchodnik && !tym.some((m) => m.id === obchodnik) && <option value={obchodnik}>…</option>}
                {tym.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          )}
          {error && <div className="t-meta text-danger">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Zrušit</button>
            <Button disabled={saving} data-testid="pripad-save">{saving ? <Spinner /> : uprava ? "Uložit" : "Založit případ"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
